import { useSearchParams } from '@hulu/react-router-dom';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import { useCallback } from 'react';

import {
  isBaymaxEnabledVar,
  onboardingActiveVar,
  RoutePath,
  selectedAdAccountIdVar,
  selectedOrganizationIdVar,
  userAmsUserIdVar,
} from '../../constants';
import { isDemoEnvironment } from '../../constants/global';
import { UserInfo } from '../../models/UserAWSCognitoInfo';
import { getRedirectSearchPath } from '../../utils/redirect';
import { HelpSiteCookieHelper } from '../../utils/user/interfaces';
import { MFAMethod } from '../useMFA/constants';
import { AwsAttributeName } from './constants';

export type CognitoError = {
  code: string;
};

export type UseAWSCognitoUser = {
  setInitialPassword: (email: string, token: string, newPassword: string) => Promise<CognitoUser>;
  login: (email: string, password: string, callback?: (error?: string) => void) => Promise<CognitoUser>;
  logout: (redirectToLogin?: boolean) => Promise<void>;
  checkSetPasswordIsNeeded: (email: string, token: string) => Promise<boolean>;
  forgotPassword: (email: string) => Promise<boolean>;
  setNewPassword: (oldPassword: string, newPassword: string, callback?: (error?: string) => void) => Promise<void>;
  setNewPasswordViaConfirmationCode: (
    email: string,
    code: string,
    newPassword: string,
    callback?: (error?: string) => void
  ) => Promise<void>;
  getUserPreferredMFAMethod: (cognitoUser: CognitoUser) => Promise<string>;
  verifySmsCode: (cognitoUser: CognitoUser, smsCode: string) => Promise<CognitoUser>;
  setAmsUser: (cognitoUser: CognitoUser) => void;
  getUserAttribute: (cognitoUser: CognitoUser, attributeName: AwsAttributeName) => Promise<string | null>;
  initiateAttributeVerification: (cognitoUser: CognitoUser, attributeName: AwsAttributeName) => Promise<void>;
  verifyAttribute: (cognitoUser: CognitoUser, attributeName: AwsAttributeName, code: string) => Promise<void>;
  setMFA: (cognitoUser: CognitoUser) => Promise<void>;
  updatePhoneNumber: (cognitoUser: CognitoUser, phoneNumber: string) => Promise<void>;
  getPreferredMFAByCognitoUser: (cognitoUser: CognitoUser) => Promise<string | undefined>;
  getAmsUser: () => Promise<UserInfo | null>;
};

const handleError = (error: unknown, callback?: (errorCode?: string) => void): void => {
  const errorCode = (error as CognitoError).code;

  callback && callback(errorCode);
};

const useAWSCognitoUser = (): UseAWSCognitoUser => {
  const [searchParams] = useSearchParams();

  const logout = useCallback(
    async (redirectToLogin = true): Promise<void> => {
      try {
        await Auth.signOut();

        selectedOrganizationIdVar(undefined);
        selectedAdAccountIdVar(undefined);
        onboardingActiveVar(undefined);
        userAmsUserIdVar(undefined);
        isBaymaxEnabledVar(false);
        HelpSiteCookieHelper.unsetHelpSiteCookie();

        if (redirectToLogin) {
          window.location.href = `${window.location.origin}${RoutePath.LOGIN}${getRedirectSearchPath(searchParams)}`;
        }
      } catch {
        console.log('Error on logout request');
      }
    },
    [searchParams]
  );

  const setAmsUser = useCallback((cognitoUser: CognitoUser): void => {
    const session = cognitoUser.getSignInUserSession();

    if (session) {
      const user: UserInfo = session.getIdToken().decodePayload() as UserInfo;
      userAmsUserIdVar(user.ams_user_id);
      selectedOrganizationIdVar(undefined);
      selectedAdAccountIdVar(undefined);
      isBaymaxEnabledVar(false);
      HelpSiteCookieHelper.setHelpSiteCookieForNonAdmin().then(null);
    }
  }, []);

  const getAmsUser = useCallback(async (): Promise<UserInfo | null> => {
    const session = await Auth.currentSession();
    if (session) {
      return session.getIdToken().decodePayload() as UserInfo;
    }

    return null;
  }, []);

  const getUserAttribute = useCallback(
    async (cognitoUser: CognitoUser, attributeName: AwsAttributeName): Promise<string | null> => {
      try {
        const userAttributes = await Auth.userAttributes(cognitoUser);

        const neededAttribute = userAttributes.find((attribute) => attribute.Name === attributeName);

        return neededAttribute ? neededAttribute.getValue() : null;
      } catch (error: unknown) {
        throw new Error((error as CognitoError).code);
      }
    },
    []
  );

  const getUserPreferredMFAMethod = useCallback(async (cognitoUser: CognitoUser): Promise<string> => {
    try {
      return await Auth.getPreferredMFA(cognitoUser);
    } catch (error: unknown) {
      throw new Error((error as CognitoError).code);
    }
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<CognitoUser> => {
    try {
      return await Auth.signIn(email, password);
    } catch (error: unknown) {
      throw new Error((error as CognitoError).code);
    }
  }, []);

  const verifySmsCode = useCallback(async (cognitoUser: CognitoUser, smsCode: string): Promise<CognitoUser> => {
    try {
      return await Auth.confirmSignIn(cognitoUser, smsCode, MFAMethod.SMS_MFA);
    } catch (error: unknown) {
      throw new Error((error as CognitoError).code);
    }
  }, []);

  const initiateAttributeVerification = useCallback(
    async (cognitoUser: CognitoUser, attributeName: AwsAttributeName): Promise<void> => {
      try {
        await Auth.verifyUserAttribute(cognitoUser, attributeName);
      } catch (error: unknown) {
        throw new Error((error as CognitoError).code);
      }
    },
    []
  );

  const updatePhoneNumber = useCallback(async (cognitoUser: CognitoUser, phoneNumber: string): Promise<void> => {
    try {
      await Auth.updateUserAttributes(cognitoUser, {
        [AwsAttributeName.PHONE_NUMBER]: phoneNumber,
      });
    } catch (error: unknown) {
      throw new Error((error as CognitoError).code);
    }
  }, []);

  const verifyAttribute = useCallback(
    async (cognitoUser: CognitoUser, attributeName: AwsAttributeName, code: string): Promise<void> => {
      try {
        await Auth.verifyUserAttributeSubmit(cognitoUser, attributeName, code);
      } catch (error: unknown) {
        throw new Error((error as CognitoError).code);
      }
    },
    []
  );

  const setMFA = useCallback(async (cognitoUser: CognitoUser): Promise<void> => {
    try {
      await Auth.setPreferredMFA(cognitoUser, MFAMethod.SMS_MFA);
    } catch (error: unknown) {
      throw new Error((error as CognitoError).code);
    }
  }, []);

  const getPreferredMFAByCognitoUser = useCallback(async (cognitoUser: CognitoUser): Promise<string | undefined> => {
    return new Promise((resolve, reject) => {
      cognitoUser.getUserData((resError, data) => {
        if (resError) {
          reject(resError);
        }

        if (data) {
          resolve(data.PreferredMfaSetting);
        }
      });
    });
  }, []);

  const setInitialPassword = useCallback(
    async (email: string, token: string, newPassword: string): Promise<CognitoUser> => {
      try {
        const authUser: CognitoUser = await Auth.signIn(email.toLowerCase(), token);
        return await Auth.completeNewPassword(authUser, newPassword);
      } catch (error: unknown) {
        throw new Error((error as CognitoError).code);
      }
    },
    []
  );

  const checkSetPasswordIsNeeded = async (email: string, token: string): Promise<boolean> => {
    if (isDemoEnvironment) {
      return false;
    }

    try {
      const cognitoUser: CognitoUser = await Auth.signIn(email.toLowerCase(), token);

      return cognitoUser.challengeName === 'NEW_PASSWORD_REQUIRED';
    } catch (error: unknown) {
      handleError(error);
    }

    return false;
  };

  const forgotPassword = async (email: string): Promise<boolean> => {
    if (isDemoEnvironment) {
      return true;
    }

    try {
      await Auth.forgotPassword(email.toLowerCase());
      return true;
    } catch (error: unknown) {
      handleError(error);
      return false;
    }
  };

  const setNewPasswordViaConfirmationCode = async (
    email: string,
    code: string,
    newPassword: string,
    callback?: (error?: string) => void
  ): Promise<void> => {
    if (isDemoEnvironment) {
      callback?.();
    }

    try {
      await Auth.forgotPasswordSubmit(email.toLowerCase(), code, newPassword);
      callback?.();
    } catch (error: unknown) {
      handleError(error, callback);
    }
  };

  const setNewPassword = async (
    oldPassword: string,
    newPassword: string,
    callback?: (error?: string) => void
  ): Promise<void> => {
    if (isDemoEnvironment) {
      callback?.();
      return;
    }

    try {
      const cognitoUser = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(cognitoUser, oldPassword, newPassword);
      callback?.();
    } catch (error: unknown) {
      handleError(error, callback);
    }
  };

  return {
    setInitialPassword,
    login,
    logout,
    checkSetPasswordIsNeeded,
    forgotPassword,
    setNewPassword,
    setNewPasswordViaConfirmationCode,
    getUserPreferredMFAMethod,
    verifySmsCode,
    setAmsUser,
    getUserAttribute,
    initiateAttributeVerification,
    verifyAttribute,
    setMFA,
    updatePhoneNumber,
    getPreferredMFAByCognitoUser,
    getAmsUser,
  };
};

export default useAWSCognitoUser;
